import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
    Plus,
    Store,
    CheckCircle,
    MapPin,
    FileText,
    CreditCard,
    Clock,
    XCircle,
    AlertTriangle,
    Loader2
} from "lucide-react";
import axios from "axios";
import AddressFormGroup from "../components/AddressFormGroup";

const API_URL = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

export default function MyStoresPage() {
    const { hasRole, activateRole } = useAuth();
    const navigate = useNavigate();
    const handlePay = (storeId) => {
        navigate(`/store-payment/${storeId}`);
    };


    const [stores, setStores] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    // Wizard State
    const [dialogOpen, setDialogOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [step, setStep] = useState(1);

    const [formData, setFormData] = useState({
        name: "",
        store_type: "SELLER", // Updated default to Uppercase to match Enum
        description: "",
        address: "",
        city: "",
        region: "",
        zipCode: "",
        countryCode: "ht",
        address_details: "",
        phone: "",
        facebook_url: "",
        selected_categories: [], // Multi-select
        lat: null,
        lng: null,
        legal_docs: null,
        legal_docs_url: null,
        // Delivery configuration (Step 4)
        delivery_type: "paid_store", // paid_store, paid_third_party, free_store, free_third_party
        free_delivery_radius: 5, // km
        base_price: 100, // HTG
        price_per_lb: 20, // HTG/lb
        price_per_km: 5, // HTG/km
        // Artisan service configuration (Step 4 - artisan only)
        working_hours_start: "09:00",
        working_hours_end: "17:00",
        service_zone_radius: 10, // km
        service_location_type: "both", // home, shop, both
        parallel_slots: 1 // number of simultaneous appointments
    });

    useEffect(() => {
        fetchStores();
        fetchCategories();
    }, []);

    const fetchStores = async () => {
        try {
            const response = await axios.get(`${API_URL}/stores/my-stores`);
            setStores(response.data);
        } catch (error) {
            console.error("Error fetching stores:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const res = await axios.get(`${API_URL}/categories`);
            setCategories(res.data);
        } catch (e) { }
    };

    // Helper function for status badges
    const getStatusBadge = (store) => {
        const statusConfig = {
            'DRAFT': {
                color: 'bg-gray-100 text-gray-700 border-gray-300',
                text: 'Brouillon',
                icon: FileText
            },
            'PENDING_APPROVAL': {
                color: store.is_paid
                    ? 'bg-yellow-100 text-yellow-800 border-yellow-300'
                    : 'bg-orange-100 text-orange-800 border-orange-300',
                text: store.is_paid ? 'En attente validation' : 'Paiement requis',
                icon: store.is_paid ? Clock : CreditCard
            },
            'ACTIVE': {
                color: 'bg-green-100 text-green-800 border-green-300',
                text: 'Active',
                icon: CheckCircle
            },
            'REJECTED': {
                color: 'bg-red-100 text-red-800 border-red-300',
                text: 'Rejetée',
                icon: XCircle
            },
            'VACATION': {
                color: 'bg-blue-100 text-blue-800 border-blue-300',
                text: 'Mode Vacances',
                icon: Clock
            },
            'SUSPENDED': {
                color: 'bg-red-100 text-red-800 border-red-300',
                text: 'Suspendue (Admin)',
                icon: AlertTriangle
            }
        };

        const config = statusConfig[store.status] || statusConfig['DRAFT'];
        const Icon = config.icon;

        return (
            <Badge variant="outline" className={`gap-1.5 ${config.color} border`}>
                <Icon className="h-3.5 w-3.5" />
                {config.text}
            </Badge>
        );
    };

    // --- Wizard Logic ---

    // Helper for conditional dashboard navigation
    const navigateToDashboard = (store) => {
        const path = store.store_type === 'ARTISAN'
            ? `/artisan-dashboard/${store.id}`
            : `/seller-dashboard/${store.id}`;
        navigate(path);
    };

    const handleNext = () => setStep(step + 1);
    const handleBack = () => setStep(step - 1);

    const toggleCategory = (catId) => {
        const current = formData.selected_categories;
        if (current.includes(catId)) {
            setFormData({ ...formData, selected_categories: current.filter(id => id !== catId) });
        } else {
            setFormData({ ...formData, selected_categories: [...current, catId] });
        }
    };

    const handleCreateStore = async (payNow = true) => {
        setCreating(true);
        try {
            const social_links = formData.facebook_url ? { facebook: formData.facebook_url } : null;

            // Prepare service_config for artisan stores
            const service_config = formData.store_type === 'ARTISAN' ? {
                working_hours: {
                    start: formData.working_hours_start,
                    end: formData.working_hours_end
                },
                service_zone_radius: formData.service_zone_radius,
                service_location_type: formData.service_location_type,
                parallel_slots: formData.parallel_slots
            } : null;

            // Derive store_category
            let store_category = "PRODUCT";
            if (formData.store_type === "ARTISAN") {
                store_category = "SERVICE";
            }
            // Generate slug
            const slug = formData.name.toLowerCase().replace(/ /g, '-').replace(/[^\w-]+/g, '');

            const storeData = {
                name: formData.name,
                slug: slug, // Added slug
                store_type: formData.store_type,
                store_category: store_category, // Added store_category
                description: formData.description,
                address: formData.address,
                city: formData.city,
                region: formData.region,
                zip_code: formData.zipCode,
                country_code: formData.countryCode,
                address_details: formData.address_details,
                phone: formData.phone,
                social_links,
                proposed_categories: formData.selected_categories,
                coordinates: (formData.lat && formData.lng) ? { lat: formData.lat, lng: formData.lng } : null,
                legal_docs_url: formData.legal_docs_url || null,
                service_config // Add service config for artisans
            };

            const response = await axios.post(`${API_URL}/stores`, storeData);
            let newStore = response.data;

            if (payNow) {
                // Navigate to payment page
                toast.success("Boutique créée. Redirection vers le paiement...");
                navigate(`/store-payment/${newStore.id}`);
                return; // Stop here, don't execute resetForm yet if redirecting
            } else {
                toast.success("Boutique créée. Paiement en attente.");
            }

            setDialogOpen(false);
            resetForm();
            fetchStores();

        } catch (error) {
            // FIX: Ensure message is a string, not an object
            let message = "Erreur lors de la création";
            if (error.response?.data?.detail) {
                const detail = error.response.data.detail;
                message = typeof detail === 'string' ? detail : JSON.stringify(detail);
            } else if (error.message) {
                message = error.message;
            }

            console.error("Create Store Error:", error);
            toast.error(message);
            setCreating(false);
        }
    };

    const resetForm = () => {
        setStep(1);
        setFormData({
            name: "",
            store_type: "SELLER",
            description: "",
            address: "",
            city: "",
            region: "",
            zipCode: "",
            countryCode: "ht",
            address_details: "",
            phone: "",
            facebook_url: "",
            selected_categories: [],
            lat: null,
            lng: null,
            legal_docs: null,
            legal_docs_url: null,
        });
        setCreating(false);
    };

    // --- Render Steps ---

    const renderStep1 = () => (
        <div className="space-y-4">
            <div className="space-y-2">
                <Label>Nom de la boutique *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Ma Boutique" />
            </div>
            <div className="space-y-2">
                <Label>Type</Label>
                <Select value={formData.store_type} onValueChange={(v) => setFormData({ ...formData, store_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="SELLER">Vendeur</SelectItem>
                        <SelectItem value="SUPPLIER">Fournisseur</SelectItem>
                        <SelectItem value="ARTISAN">Artisan</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div className="space-y-4">
            <Label>Quelles catégories vendez-vous ?</Label>
            <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto border p-2 rounded">
                {categories.map(cat => (
                    <div key={cat.id} className="flex items-center space-x-2">
                        <input
                            type="checkbox"
                            id={cat.id}
                            checked={formData.selected_categories.includes(cat.id)}
                            onChange={() => toggleCategory(cat.id)}
                            className="rounded border-gray-300"
                        />
                        <label htmlFor={cat.id} className="text-sm cursor-pointer select-none">{cat.name}</label>
                    </div>
                ))}
            </div>
        </div>
    );

    const renderStep3 = () => (
        <div className="space-y-4">
            <Label>Localisation Complète</Label>
            <AddressFormGroup
                addressData={{
                    countryCode: formData.countryCode,
                    address: formData.address,
                    city: formData.city,
                    region: formData.region,
                    zipCode: formData.zipCode,
                    address_details: formData.address_details,
                    lat: formData.lat,
                    lng: formData.lng
                }}
                onChange={(newData) => {
                    setFormData({
                        ...formData,
                        countryCode: newData.countryCode,
                        address: newData.address,
                        city: newData.city,
                        region: newData.region,
                        zipCode: newData.zipCode,
                        address_details: newData.address_details,
                        lat: newData.lat,
                        lng: newData.lng
                    });
                }}
            />
        </div>
    );

    const renderArtisanServiceConfig = () => (
        <div className="space-y-4">
            <div>
                <Label className="text-lg font-semibold">Configuration du Service</Label>
                <p className="text-sm text-gray-500 mt-1">Paramètres spécifiques pour votre activité artisanale</p>
            </div>

            {/* Horaires de travail */}
            <div className="space-y-2">
                <Label>Horaires de Travail</Label>
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <Label className="text-xs text-gray-500">Ouverture</Label>
                        <Input
                            type="time"
                            value={formData.working_hours_start}
                            onChange={(e) => setFormData({ ...formData, working_hours_start: e.target.value })}
                        />
                    </div>
                    <div>
                        <Label className="text-xs text-gray-500">Fermeture</Label>
                        <Input
                            type="time"
                            value={formData.working_hours_end}
                            onChange={(e) => setFormData({ ...formData, working_hours_end: e.target.value })}
                        />
                    </div>
                </div>
            </div>

            {/* Zone d'intervention */}
            <div className="space-y-2">
                <Label>Zone d'Intervention</Label>
                <div className="flex items-center gap-2">
                    <Input
                        type="number"
                        value={formData.service_zone_radius}
                        onChange={(e) => setFormData({ ...formData, service_zone_radius: Number(e.target.value) })}
                        placeholder="10"
                        className="w-24"
                    />
                    <span className="text-sm text-gray-500">km autour de votre localisation</span>
                </div>
            </div>

            {/* Type de service */}
            <div className="space-y-2">
                <Label>Où fournissez-vous vos services ?</Label>
                <Select
                    value={formData.service_location_type}
                    onValueChange={(v) => setFormData({ ...formData, service_location_type: v })}
                >
                    <SelectTrigger>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="home">
                            <div className="flex flex-col items-start">
                                <span className="font-medium">Je me déplace chez le client</span>
                                <span className="text-xs text-gray-500">Service à domicile uniquement</span>
                            </div>
                        </SelectItem>
                        <SelectItem value="shop">
                            <div className="flex flex-col items-start">
                                <span className="font-medium">Client vient chez moi</span>
                                <span className="text-xs text-gray-500">Dans mon local/atelier</span>
                            </div>
                        </SelectItem>
                        <SelectItem value="both">
                            <div className="flex flex-col items-start">
                                <span className="font-medium">Les deux</span>
                                <span className="text-xs text-gray-500">Flexible selon le client</span>
                            </div>
                        </SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {/* Créneaux simultanés */}
            <div className="space-y-2">
                <Label>Nombre de créneaux simultanés</Label>
                <Input
                    type="number"
                    min="1"
                    value={formData.parallel_slots}
                    onChange={(e) => setFormData({ ...formData, parallel_slots: Number(e.target.value) })}
                    placeholder="1"
                />
                <p className="text-xs text-gray-500">
                    Combien de clients pouvez-vous servir en même temps ? (Ex: 2 coiffeurs = 2 créneaux)
                </p>
            </div>

            <div className="p-3 border border-blue-200 rounded bg-blue-50">
                <p className="text-sm text-blue-800">
                    💡 Ces paramètres seront utilisés pour générer automatiquement votre calendrier de réservation.
                </p>
            </div>
        </div>
    );

    const renderStep4 = () => (
        <div className="space-y-6">
            <div className="p-4 border rounded bg-blue-50 border-blue-100">
                <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                    <FileText className="h-4 w-4" /> Vérification Légale
                </h4>
                <p className="text-sm text-blue-600 mb-4">
                    Pour activer votre boutique, nous avons besoin d'une preuve d'identité ou de patente.
                </p>
                <Input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        if (file.size > 5 * 1024 * 1024) { toast.error("Fichier trop volumineux (> 5MB)"); return; }
                        try {
                            const fd = new FormData();
                            fd.append("file", file);
                            const res = await axios.post(`${API_URL}/uploads/?folder=documents`, fd);
                            setFormData(prev => ({ ...prev, legal_docs: file, legal_docs_url: res.data.url }));
                            toast.success("Document téléchargé !");
                        } catch {
                            toast.error("Erreur lors de l'envoi du document");
                        }
                    }}
                />
                {formData.legal_docs_url && (
                    <p className="text-xs text-green-600 mt-1">✓ Document reçu : {formData.legal_docs?.name}</p>
                )}
            </div>

            <div className="p-4 border rounded bg-yellow-50 border-yellow-100">
                <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                    <CreditCard className="h-4 w-4" /> Activation Payante
                </h4>
                <p className="text-sm text-yellow-600 mb-2">
                    Frais d'activation annuel : <strong>2500 HTG</strong>
                </p>
                <p className="text-xs text-gray-500">
                    Votre boutique sera créée en attente de validation. Le paiement sera traité à l'étape suivante.
                </p>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-background">
            <Navbar />

            <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-8">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground mb-2">
                            Mes boutiques
                        </h1>
                        <p className="text-muted-foreground">
                            Gérez vos boutiques et vos produits
                        </p>
                    </div>

                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button onClick={resetForm}>
                                <Plus className="mr-2 h-4 w-4" />
                                Créer une boutique
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl flex flex-col max-h-[90vh]">
                            <DialogHeader className="shrink-0">
                                <DialogTitle>Assistant Création Boutique - Étape {step}/{formData.store_type === 'ARTISAN' ? 5 : 4}</DialogTitle>
                            </DialogHeader>

                            <div className="flex-1 overflow-y-auto py-4 pr-1">
                                {step === 1 && renderStep1()}
                                {step === 2 && renderStep2()}
                                {step === 3 && renderStep3()}
                                {step === 4 && formData.store_type === 'ARTISAN' && renderArtisanServiceConfig()}
                                {step === 4 && formData.store_type !== 'ARTISAN' && renderStep4()}
                                {step === 5 && formData.store_type === 'ARTISAN' && renderStep4()}
                            </div>

                            <div className="flex justify-between shrink-0 pt-4 border-t">
                                <Button variant="outline" onClick={handleBack} disabled={step === 1}>Précédent</Button>

                                {step < (formData.store_type === 'ARTISAN' ? 5 : 4) ? (
                                    <Button onClick={handleNext}>Suivant</Button>
                                ) : (
                                    <div className="flex gap-2">
                                        <Button variant="ghost" onClick={() => handleCreateStore(false)} disabled={creating}>
                                            Payer plus tard
                                        </Button>
                                        <Button onClick={() => handleCreateStore(true)} disabled={creating} className="bg-green-600 hover:bg-green-700">
                                            {creating ? "Traitement..." : "Payer & Créer (2500 HTG)"}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </DialogContent>
                    </Dialog>
                </div>

                {/* Existing Store List Logic */}
                {/* ... Reuse previous card logic but add STATUS BADGES ... */}
                {/* Simplified List for clarity in this update */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {stores.map(store => (
                        <Card key={store.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                            {/* Banner and Logo */}
                            <div className="h-32 bg-gray-200 relative">
                                {store.banner_url ? (
                                    <img src={store.banner_url} alt="Bannière" className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                                        <Store className="h-12 w-12 opacity-20" />
                                    </div>
                                )}

                                {/* Status Overlays */}
                                {!store.is_paid && store.status !== 'REJECTED' && (
                                    <div className="absolute inset-0 bg-gray-800/80 flex items-center justify-center flex-col gap-2 p-4">
                                        <span className="text-white font-bold">Paiement en attente</span>
                                        <Button size="sm" onClick={() => handlePay(store.id)} className="bg-green-600 hover:bg-green-700">
                                            Payer (2500 HTG)
                                        </Button>
                                    </div>
                                )}
                                {store.is_paid && store.status === 'PENDING_APPROVAL' && (
                                    <div className="absolute inset-0 bg-yellow-500/80 flex items-center justify-center text-white font-bold">
                                        En attente de validation
                                    </div>
                                )}


                                {/* Logo Overlay */}
                                <div className="absolute -bottom-8 left-6">
                                    <div className="w-16 h-16 rounded-full border-4 border-white bg-white shadow-md overflow-hidden">
                                        {store.logo_url ? (
                                            <img src={store.logo_url} alt="Logo" className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center bg-gray-100 text-xl font-bold text-gray-500">
                                                {store.name.charAt(0)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <CardContent className="p-6 pt-10">
                                <h3 className="font-bold text-lg">{store.name}</h3>
                                <div className="flex gap-2 mb-4 mt-2">
                                    <Badge variant="outline">{store.store_type}</Badge>
                                    {getStatusBadge(store)}
                                </div>

                                {/* Conditional Actions Based on Status */}
                                {store.status === 'DRAFT' && (
                                    <Button className="w-full" variant="outline" onClick={() => navigateToDashboard(store)}>
                                        <FileText className="mr-2 h-4 w-4" />
                                        Compléter la demande
                                    </Button>
                                )}

                                {store.status === 'PENDING_APPROVAL' && !store.is_paid && (
                                    <div className="space-y-2">
                                        <Button className="w-full bg-green-600 hover:bg-green-700" onClick={() => handlePay(store.id)}>
                                            <CreditCard className="mr-2 h-4 w-4" />
                                            Payer 2500 HTG
                                        </Button>
                                        <Button className="w-full" variant="outline" size="sm" onClick={() => navigateToDashboard(store)}>
                                            Modifier avant paiement
                                        </Button>
                                    </div>
                                )}

                                {store.status === 'PENDING_APPROVAL' && store.is_paid && (
                                    <div className="w-full p-3 bg-yellow-50 border border-yellow-200 rounded-md text-center">
                                        <Clock className="h-5 w-5 mx-auto mb-1 text-yellow-600" />
                                        <p className="text-sm text-yellow-800 font-medium">En attente validation admin</p>
                                        <p className="text-xs text-yellow-600 mt-1 mb-2">Vous serez notifié sous 48h</p>
                                        <Button className="w-full" variant="outline" size="sm" onClick={() => navigateToDashboard(store)}>
                                            Accéder au Dashboard
                                        </Button>
                                    </div>
                                )}

                                {store.status === 'ACTIVE' && (
                                    <Button className="w-full" onClick={() => navigateToDashboard(store)}>
                                        <Store className="mr-2 h-4 w-4" />
                                        {store.store_type === 'ARTISAN' ? 'Gérer mes services' : 'Préparer l\'inventaire'}
                                    </Button>
                                )}

                                {(store.status === 'REJECTED' || store.status === 'SUSPENDED') && (
                                    <div className="space-y-2">
                                        <div className="w-full p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                                            <strong>Raison:</strong> {store.rejection_reason || "Documents manquants ou invalides"}
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <Button variant="outline" size="sm" onClick={() => navigateToDashboard(store)}>
                                                Accéder au Dashboard
                                            </Button>
                                            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handlePay(store.id)}>
                                                Payer (2500 G)
                                            </Button>
                                        </div>
                                        <p className="text-[10px] text-center text-gray-500">
                                            {store.status === 'REJECTED' ? "Remboursé. Paiement requis pour nouvelle demande." : "Boutique suspendue. Payez pour réactiver."}
                                        </p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </div>
            <Footer />
        </div>
    );
}
